import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import type { TaskPriority } from '../types';
import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';
import { useMe } from '../hooks/useMe';

interface Props {
  open: boolean;
  /**
   * Default group to pre-select. Pass the current view's `teamId` (company
   * slug or `personal:<sub>`). `null` = no useful default — the modal will
   * pick the caller's first visible group alphabetically.
   */
  defaultTeamId: string | null;
  onClose: () => void;
  onCreate: (input: {
    teamId: string;
    title: string;
    priority: TaskPriority;
    dueDate?: string;
    note?: string;
    assigneeId?: string;
    private?: boolean;
  }) => Promise<void>;
}

export function NewTaskModal({ open, defaultTeamId, onClose, onCreate }: Props) {
  const [title, setTitle] = useState('');
  const [teamId, setTeamId] = useState<string>(defaultTeamId ?? '');
  const [priority, setPriority] = useState<TaskPriority>('low');
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [assigneeId, setAssigneeId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { members } = useTeam();
  const { user } = useAuth();
  const { me } = useMe();

  // Visible groups, alphabetical by displayName. Personal groups stay in the
  // list; the caller's own personal partition is the implicit "Personal"
  // default when `defaultTeamId` is missing or `'personal'`.
  const groups = useMemo(() => {
    const gs = me?.groups ?? [];
    return [...gs].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [me]);

  const ownPersonalId = useMemo(() => {
    const own = me?.groups.find((g) => g.kind === 'personal' && g.id === `personal:${me.sub}`);
    return own?.id ?? me?.groups.find((g) => g.kind === 'personal')?.id ?? '';
  }, [me]);

  const resolvedDefault = useMemo(() => {
    if (defaultTeamId && groups.some((g) => g.id === defaultTeamId)) return defaultTeamId;
    // "personal" shorthand → caller's own personal partition.
    if (defaultTeamId === 'personal' && ownPersonalId) return ownPersonalId;
    // Combined / unknown → first company alphabetically, else first group.
    const firstCompany = groups.find((g) => g.kind === 'company');
    return firstCompany?.id ?? groups[0]?.id ?? ownPersonalId ?? '';
  }, [defaultTeamId, groups, ownPersonalId]);

  useEffect(() => {
    if (open) {
      setTeamId(resolvedDefault);
      setTitle('');
      setPriority('low');
      setDueDate('');
      setNote('');
      setIsPrivate(false);
      setAssigneeId(user?.userId ?? '');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, resolvedDefault, user]);

  if (!open) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t || busy || !teamId) return;
    setBusy(true);
    try {
      await onCreate({
        teamId,
        title: t,
        priority,
        dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
        note: note.trim() || undefined,
        assigneeId: assigneeId || undefined,
        private: isPrivate,
      });
      onClose();
    } finally {
      setBusy(false);
    }
  }

  const currentGroupLabel =
    groups.find((g) => g.id === teamId)?.displayName ?? teamId;

  return (
    <div
      className="new-task-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="New task"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <form className="new-task-dialog" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <header>
          <h3>New task</h3>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </header>
        <label>
          <span>Title</span>
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to happen?"
            required
          />
        </label>
        <div className="new-task-grid">
          {groups.length >= 2 && (
            <label>
              <span>Group</span>
              <select
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                required
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.displayName}</option>
                ))}
              </select>
            </label>
          )}
          <label>
            <span>Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
          <label>
            <span>Due date</span>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
          {members.length > 1 && (
            <label>
              <span>Assignee</span>
              <select
                value={assigneeId || user?.userId || ''}
                disabled={isPrivate}
                onChange={(e) => setAssigneeId(e.target.value)}
                title={isPrivate ? 'Private tasks are assigned to you' : undefined}
              >
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.displayName}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <label className="new-task-private-row">
          <input
            type="checkbox"
            checked={isPrivate}
            onChange={(e) => {
              setIsPrivate(e.target.checked);
              if (e.target.checked) setAssigneeId(user?.userId ?? '');
            }}
          />
          <span>Private — only you will see this task</span>
        </label>
        <label>
          <span>Note (optional)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Any context — the task will start in Pending if you add one"
            rows={3}
          />
        </label>
        <div className="new-task-footer">
          <span className="muted small">Adding to {currentGroupLabel}</span>
          <button type="submit" className="primary" disabled={busy || !title.trim() || !teamId}>
            {busy ? 'Creating…' : 'Create task'}
          </button>
        </div>
      </form>
    </div>
  );
}
